import { Box, IconButton, Popper, Tooltip, ClickAwayListener, useTheme, Button, Dialog, Typography } from "@mui/material"
import React, { useRef, useState } from "react"
import { observer } from "mobx-react"

import { useVirtualboardStore } from "../../store/virtualboard.store"
import { Icons } from "../../utils/virtualboard-utilities"

const MenuBar = observer( ( { readOnly = false, onChange, downloadMergedCanvas, toggleFullScreen } ) => {

    const [showClearAnnotationsConfirmationDialog, setShowClearAnnotationsConfirmationDialog] = useState( false )
    const [strokeOptionsOpen, setStrokeOptionsOpen] = useState( false )

    const menuBarRef = useRef()

    const getToogleBtnStyles = ( active = false ) => ( {
        aspectRatio: "1 / 1",
        borderRadius: "5px",
        fontSize: "18px",
        bgcolor: active ? palette.primary.main : "",
        color: active ? "white" : palette.common.font,
        border: active ? `1px solid #fff0` : border[1],
        ":hover": { bgcolor: active ? palette.primary.main : "" },
    } )

    const { palette, border, shadows } = useTheme()
    const store = useVirtualboardStore()
    const paletteBtnRef = useRef( null )

    const clearCanvas = () => {
        store.clear()
        setShowClearAnnotationsConfirmationDialog( false )
        store.addEvent( { type: "clear-canvas" } )
        if ( onChange && typeof onChange === 'function' )
            onChange( { type: "clear-canvas" }, { elements: [], pageId: store.getBoardState?.document?.slides[store.getBoardState?.currentSlide].id } )

    }

    const undo = () => {
        const events = store.addEvent( { type: "undo" } )
        if ( onChange && typeof onChange === 'function' && events )
            onChange( { type: "undo" }, { elements: store.getElements, eventsList: events, pageId: store.getBoardState?.document?.slides[store.getBoardState?.currentSlide].id } )

    }
    const redo = () => {
        const events = store.addEvent( { type: "redo" } )
        if ( onChange && typeof onChange === 'function' && events )
            onChange( { type: "redo" }, { elements: store.getElements, eventsList: events, pageId: store.getBoardState?.document?.slides[store.getBoardState?.currentSlide].id } )

    }


    return (
        <Box
            ref={menuBarRef}
            boxShadow={shadows[1]}
            bgcolor={palette.background.paper}
            zIndex={10}
            position="absolute"
            display="flex"
            alignItems="center"
            gap="5px"
            top="10px"
            right="10px"
            padding="5px 10px"
            borderRadius="10px"
            maxHeight="100%"
            maxWidth="1000px"
            flexWrap="wrap"
            width="auto"
        >

            {/* Clear Annotations Confirm Dialog */}
            {!readOnly && <Dialog
                open={showClearAnnotationsConfirmationDialog}
                onClose={() => setShowClearAnnotationsConfirmationDialog( false )}
                container={menuBarRef.current}
                slotProps={{
                    paper: {
                        sx: {
                            width: "90%",
                            maxWidth: "350px"
                        }
                    }
                }}
            >
                <Box>
                    <Typography variant='h6' component="h1" padding="10px 20px" bgcolor={palette.error.main} color={palette.error.contrastText}>
                        Are you sure?
                    </Typography>
                    <Typography padding="20px" variant='body2'>
                        Once cleared the annotations cannot be retrieved again!
                    </Typography>
                    <Box justifyContent="flex-end" display="flex" gap="10px" borderTop={border[1]} padding="10px" alignItems="center">
                        <Button onClick={() => setShowClearAnnotationsConfirmationDialog( false )} color='greyed' sx={{ borderRadius: "20px", ":hover": { color: palette.primary.main } }} size='small' >
                            Cancel
                        </Button>
                        <Button onClick={clearCanvas} variant='contained' disableElevation color='error' size='small' >
                            Yes, Clear Annotations
                        </Button>
                    </Box>
                </Box>
            </Dialog>}

            {/* === Color & Stroke Popper === */}
            <Popper
                container={menuBarRef.current}
                sx={{ zIndex: "10" }}
                anchorEl={paletteBtnRef.current}
                open={strokeOptionsOpen}
                placement="bottom"
            >
                <ClickAwayListener
                    onClickAway={() => setStrokeOptionsOpen( false )}
                >
                    <Box width="max-content" borderRadius="10px" marginRight="10px" marginTop="10px" bgcolor={palette.background.paper} display="flex" flexDirection="column" boxShadow={shadows[1]}>
                        <Button onClick={toggleFullScreen} color='greyed' sx={{ gap: "10px", fontSize: "12px", padding: "5px 20px", borderRadius: "0", alignItems: "center", textAlign: "right", display: "flex", justifyContent: "flex-start" }} size='small' >
                            <Box fontSize="18px">{Icons.default.Expand}</Box>
                            {store.getContainerState?.fullscreen ? "Exit" : "Enter"} full screen
                        </Button>
                        <Button onClick={downloadMergedCanvas} color='greyed' sx={{ gap: "10px", fontSize: "12px", padding: "5px 20px", borderRadius: "0", alignItems: "center", textAlign: "right", display: "flex", justifyContent: "flex-start" }} size='small' >
                            <Box fontSize="18px">{Icons.default.Download}</Box>
                            Download current page
                        </Button>
                        {!readOnly && <Button color='greyed' onClick={() => store.setContainerState( { hideTools: !store.getContainerState?.hideTools } )} sx={{ gap: "10px", fontSize: "12px", padding: "5px 20px", borderRadius: "0", alignItems: "center", textAlign: "right", display: "flex", justifyContent: "flex-start" }} size='small' >
                            <Box fontSize="18px">{Icons.default.Tools}</Box>
                            {store.getContainerState?.hideTools ? "Show" : "Hide"}  toolbar
                        </Button>}
                        {!readOnly && <Button onClick={() => setShowClearAnnotationsConfirmationDialog( true )} color='greyed' sx={{ gap: "10px", fontSize: "12px", padding: "5px 20px", borderRadius: "0", alignItems: "center", textAlign: "right", display: "flex", justifyContent: "flex-start" }} size='small' >
                            <Box fontSize="18px">{Icons.default.Broom}</Box>
                            Clear Annotations
                        </Button>}
                    </Box>
                </ClickAwayListener>
            </Popper>

            {!readOnly && <Tooltip placement='bottom' disableInteractive title="Undo">
                <IconButton
                    sx={getToogleBtnStyles( false )}
                    onClick={undo}
                >
                    {Icons.default.Undo}
                </IconButton>
            </Tooltip>}
            {!readOnly && <Tooltip placement="bottom" disableInteractive title="Redo">
                <IconButton
                    sx={getToogleBtnStyles( false )}
                    onClick={redo}
                >
                    {Icons.default.Redo}
                </IconButton>
            </Tooltip>}
            <Tooltip placement="left" disableInteractive title="More Options">
                <IconButton
                    ref={paletteBtnRef}
                    onClick={() => {
                        setStrokeOptionsOpen( true )
                    }}
                >
                    {Icons.default.MoreOptions}
                </IconButton>
            </Tooltip>

        </Box>
    )
} )

export default MenuBar
